
import { defineComponent, ref, computed, PropType, Ref, watch } from "vue";
import {
  useGetProfilesByLevelOfUseQuery,
  ProfileType,
  LevelOfUse,
  GetProfilesByLevelOfUseQuery,
} from "@/graphql/types";
import { useResult } from "@vue/apollo-composable";
import VDPopupListbox from "./VDPopupListbox.vue";
import { VDPopupListboxClass } from "./VDPopupListbox";
import GroupedItems from "@/models/GroupedItems";

export default defineComponent({
  name: "RoleProfilePicker",
  components: {
    VDPopupListbox,
  },
  emits: ["profileSelected"],
  props: {
    disabled: {
      type: Boolean,
      default: false,
    },
    addedProfiles: {
      type: Array as PropType<ProfileType[]>,
      default: () => [],
    },
  },
  setup() {
    // VDOM
    const listbox = ref<VDPopupListboxClass>();

    // Refs
    const isEnabled = ref(false);

    // Data - Query for organization-level profiles
    const getProfilesResponse = useGetProfilesByLevelOfUseQuery(
      () => ({ levelOfUse: LevelOfUse.Organization }),
      () => ({
        enabled: isEnabled.value,
        fetchPolicy: "cache-first",
        errorPolicy: "all",
      })
    );

    // Computed
    const loading = computed(() => {
      return getProfilesResponse.loading.value;
    });

    const error = computed(() => {
      return getProfilesResponse.error.value;
    });

    return {
      groupedProfiles: useGroupedProfilesByApplication(
        getProfilesResponse.result
      ),
      listbox,
      loading,
      error,
      toggle: (event: Event) => {
        isEnabled.value = true;
        listbox.value?.toggle(event);
      },
    };
  },
});

function useGroupedProfilesByApplication(
  profilesQuery: Ref<GetProfilesByLevelOfUseQuery | undefined>
) {
  const groupedProfiles = ref<GroupedItems<ProfileType>[]>([]);

  watch(
    () => profilesQuery.value,
    (newValue) => {
      const map: { [name: string]: ProfileType[] } = {};

      newValue?.profilesByLevelOfUse?.forEach((element: any) => {
        if (element?.application?.name && map[element.application.name]) {
          map[element.application.name].push(element);
        } else if (element?.application?.name) {
          map[element.application.name] = [element];
        }
      });

      groupedProfiles.value = Object.keys(map)
        .sort()
        .map((element) => {
          return {
            name: element,
            items: map[element].sort(
              (a, b) => a.name?.localeCompare(b.name ?? "") ?? -1
            ),
          };
        });
    },
    { deep: true }
  );

  return groupedProfiles;
}

// Export type for parent components
export declare class RoleProfilePickerClass {
  toggle(event: Event): void;
  hide(): void;
}
